import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";

// Mic button that records audio in the browser, uploads to /api/voice/transcribe,
// and calls onTranscript(text) once done. Pauses gracefully if mic isn't available.
export default function VoiceRecorder({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const start = async () => {
    if (recording || uploading || disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime =
        typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        if (blob.size < 800) {
          toast.error("Recording too short");
          return;
        }
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            onTranscript(data.text);
          } else {
            toast.error("No speech detected");
          }
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setUploading(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone not available");
    }
  };

  const stop = () => {
    if (!recording) return;
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* ignore */
    }
    setRecording(false);
  };

  if (uploading) {
    return (
      <div
        data-testid="voice-uploading"
        className="w-11 h-11 rounded-full bg-[#F2EFE9] flex items-center justify-center text-[#6B655D]"
        aria-label="Transcribing"
      >
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={recording ? "voice-stop-btn" : "voice-record-btn"}
      onClick={recording ? stop : start}
      disabled={disabled}
      aria-label={recording ? "Stop recording" : "Record voice note"}
      className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
        recording
          ? "bg-[#D98074] text-white hover:bg-[#C26658] animate-pulse"
          : "bg-[#F2EFE9] text-[#2D2926] hover:bg-[#E6E2D8]"
      }`}
    >
      {recording ? (
        <Square size={16} strokeWidth={2} fill="currentColor" />
      ) : (
        <Mic size={18} strokeWidth={1.5} />
      )}
    </button>
  );
}
